import { useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetFile } from '@/hooks/useFiles';
import type { EnrichedVulnerability, Weakness } from '@/sections/threats/types';
import cweFrameworks from '@/sections/vulnerabilities/data/cweFrameworks.json';
import type { RiskFrameworkEnrichment } from '@/sections/vulnerabilities/types';
import {
  parseRiskFrameworkEnrichment,
  riskFrameworkS3Key,
} from '@/sections/vulnerabilities/utils/frameworkEnrichment.util';
import { useAuth } from '@/state/auth';
import type { Risk, Setting } from '@/types';
import { cn } from '@/utils/classname';

// ---------------------------------------------------------------------------
// Client-side CWE → framework derivation (fallback when S3 file is missing)
// ---------------------------------------------------------------------------

type CWEFrameworkEntry = Partial<Record<FrameworkKey, string>>;
const CWE_FRAMEWORKS = cweFrameworks as Record<string, CWEFrameworkEntry>;

function deriveFromWeaknesses(
  weaknesses: Weakness[]
): Pick<RiskFrameworkEnrichment, FrameworkKey> | null {
  const result: Record<FrameworkKey, Set<string>> = {
    nist_csf: new Set(),
    pci_dss: new Set(),
    hipaa: new Set(),
    soc2: new Set(),
    cis: new Set(),
  };

  for (const w of weaknesses) {
    const entry = CWE_FRAMEWORKS[w.value];
    if (!entry) continue;
    for (const fw of FRAMEWORK_KEYS) {
      const control = entry[fw];
      if (control) result[fw].add(control);
    }
  }

  const hasAny = FRAMEWORK_KEYS.some(fw => result[fw].size > 0);
  if (!hasAny) return null;

  return {
    nist_csf: [...result.nist_csf],
    pci_dss: [...result.pci_dss],
    hipaa: [...result.hipaa],
    soc2: [...result.soc2],
    cis: [...result.cis],
  };
}

// ---------------------------------------------------------------------------
// Framework metadata
// ---------------------------------------------------------------------------

const FRAMEWORKS = [
  { key: 'nist_csf', label: 'NIST CSF 2.0' },
  { key: 'pci_dss', label: 'PCI DSS 4.0' },
  { key: 'hipaa', label: 'HIPAA Security Rule' },
  { key: 'soc2', label: 'SOC 2 Type II' },
  { key: 'cis', label: 'CIS Controls v8' },
] as const;

type FrameworkKey = (typeof FRAMEWORKS)[number]['key'];

const FRAMEWORK_KEYS: FrameworkKey[] = FRAMEWORKS.map(f => f.key);

const FRAMEWORK_LABELS: Record<string, string> = Object.fromEntries(
  FRAMEWORKS.map(f => [f.key, f.label])
);

function getControlUrl(fw: FrameworkKey, control: string): string | null {
  switch (fw) {
    case 'hipaa': {
      const m = control.match(/§(164\.\d+)(\([^)]*\)(?:\([^)]*\))*)/);
      if (m)
        return `https://www.ecfr.gov/current/title-45/section-${m[1]}#p-${m[1]}${m[2]}`;
      const s = control.match(/§(164\.\d+)/);
      if (s) return `https://www.ecfr.gov/current/title-45/section-${s[1]}`;
      return null;
    }
    case 'nist_csf': {
      const m = control.match(/^([A-Z]{2})\.([A-Z]{2})/);
      if (m) {
        const fn = m[1].toLowerCase();
        const cat = `${fn}-${m[2].toLowerCase()}`;
        return `https://csf.tools/reference/nist-cybersecurity-framework/v2-0/${fn}/${cat}/`;
      }
      return null;
    }
    case 'pci_dss': {
      const req = control.match(/^(\d+)/);
      if (req)
        return `https://www.pcisecuritystandards.org/document_library/?document=pci_dss#pci-dss-req-${req[1]}`;
      return null;
    }
    case 'soc2':
      return 'https://www.aicpa-cima.com/resources/landing/system-and-organization-controls-soc-suite-of-services';
    case 'cis':
      return 'https://www.cisecurity.org/controls/v8';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ComplianceFrameworksPanelProps {
  risk: Risk;
  threatIntel: EnrichedVulnerability;
}

export const ComplianceFrameworksPanel = ({
  risk,
  threatIntel,
}: ComplianceFrameworksPanelProps) => {
  const { me, friend, isImpersonating } = useAuth();
  const email = isImpersonating ? friend : undefined;

  const { data: settings } = useMy(
    { resource: 'setting' },
    { email: email || me, staleTime: 30000, gcTime: 300000 }
  );

  const { data: enrichmentRaw, isFetching } =
    useGetFile<RiskFrameworkEnrichment>(
      { name: risk.name ? riskFrameworkS3Key(risk.name) : '' },
      { enabled: !!risk.name }
    );

  const s3Enrichment = useMemo(
    () => parseRiskFrameworkEnrichment(enrichmentRaw),
    [enrichmentRaw]
  );

  const weaknesses = threatIntel.weaknesses || [];

  const clientDerived = useMemo(
    () => deriveFromWeaknesses(weaknesses),
    [weaknesses]
  );

  const enrichment = useMemo(() => {
    if (!s3Enrichment) return clientDerived;
    if (!clientDerived) return s3Enrichment;
    const merged: Pick<RiskFrameworkEnrichment, FrameworkKey> &
      Omit<RiskFrameworkEnrichment, FrameworkKey> = { ...s3Enrichment };
    for (const fw of FRAMEWORK_KEYS) {
      if (!merged[fw] || merged[fw]!.length === 0) {
        merged[fw] = clientDerived[fw] ?? [];
      }
    }
    return merged;
  }, [s3Enrichment, clientDerived]);

  const enabledFrameworks: FrameworkKey[] = useMemo(() => {
    const setting = (settings || []).find(
      (s: Setting) => s.name === 'compliance_frameworks'
    );
    if (!setting?.value) return [];
    if (Array.isArray(setting.value))
      return (setting.value as string[]).filter((k): k is FrameworkKey =>
        FRAMEWORK_KEYS.includes(k as FrameworkKey)
      );
    return [];
  }, [settings]);

  const frameworksToShow =
    enabledFrameworks.length > 0
      ? FRAMEWORK_KEYS.filter(k => enabledFrameworks.includes(k))
      : [...FRAMEWORK_KEYS];

  if (isFetching && !clientDerived) {
    return (
      <div className="text-chariot-text-secondary py-8 text-center text-sm">
        Loading compliance data...
      </div>
    );
  }

  if (!enrichment || weaknesses.length === 0) {
    return (
      <div className="text-chariot-text-secondary py-8 text-center text-sm">
        No compliance framework data available for this vulnerability.
      </div>
    );
  }

  const getControls = (fw: FrameworkKey): string[] => enrichment[fw] ?? [];

  const totalImpacted = frameworksToShow.reduce(
    (sum, fw) => sum + getControls(fw).length,
    0
  );

  return (
    <div className="flex flex-col gap-6">
      {/* CWE Weaknesses */}
      <div className="flex flex-col gap-2">
        <h4 className="text-chariot-text-primary text-sm font-semibold">
          CWE Weaknesses
        </h4>
        <div className="flex flex-wrap gap-2">
          {weaknesses.map((w: Weakness) => (
            <a
              key={`${w.source}-${w.value}`}
              href={w.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-chariot-background-secondary text-chariot-text-primary border-chariot-stroke hover:border-chariot-brand-primary inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
            >
              {w.value}: {w.name}
            </a>
          ))}
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-3">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {totalImpacted} control{totalImpacted !== 1 ? 's' : ''} impacted
        </span>
        <span className="text-chariot-text-secondary text-xs">
          across {frameworksToShow.length} framework
          {frameworksToShow.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Framework → controls table */}
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="bg-chariot-background-secondary">
              <th className="text-chariot-text-secondary px-3 py-2 font-medium">
                Framework
              </th>
              <th className="text-chariot-text-secondary px-3 py-2 font-medium">
                Impacted Controls
              </th>
              <th className="text-chariot-text-secondary w-24 px-3 py-2 text-center font-medium">
                Count
              </th>
            </tr>
          </thead>
          <tbody>
            {frameworksToShow.map((fw, i) => {
              const controls = getControls(fw);
              return (
                <tr
                  key={fw}
                  className={cn(
                    'border-chariot-stroke border-t',
                    i % 2 === 0
                      ? 'bg-chariot-background-primary'
                      : 'bg-chariot-background-secondary/30'
                  )}
                >
                  <td className="text-chariot-text-primary px-3 py-2.5 font-medium whitespace-nowrap">
                    {FRAMEWORK_LABELS[fw]}
                  </td>
                  <td className="px-3 py-2.5">
                    {controls.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {controls.map(c => {
                          const url = getControlUrl(fw, c);
                          return url ? (
                            <a
                              key={c}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-chariot-brand-primary/15 text-chariot-text-primary border-chariot-brand-primary/30 hover:border-chariot-brand-primary hover:bg-chariot-brand-primary/25 rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-colors"
                            >
                              {c}
                            </a>
                          ) : (
                            <span
                              key={c}
                              className="bg-chariot-brand-primary/15 text-chariot-text-primary border-chariot-brand-primary/30 rounded border px-1.5 py-0.5 text-[10px] font-semibold"
                            >
                              {c}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-chariot-text-secondary italic">
                        No mapped controls
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span
                      className={cn(
                        'inline-flex size-6 items-center justify-center rounded-full text-[10px] font-bold',
                        controls.length > 0
                          ? 'bg-chariot-brand-primary/15 text-chariot-text-primary'
                          : 'bg-chariot-background-secondary text-chariot-text-secondary'
                      )}
                    >
                      {controls.length}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
